// src/pages/ReportsPage.js
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ActivityList from '../components/ActivityLists'; // [marker: fixes-oct21 v1]

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('membership');
  const [byType, setByType] = useState([]);
  const [bySalesperson, setBySalesperson] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [eventsSummary, setEventsSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const year = new Date().getFullYear();

  useEffect(() => {
    setLoading(true);
    const calls = {
      membership: [api.get('/reports/membership/by-type'), api.get('/reports/membership/by-salesperson')],
      financial: [api.get(`/reports/financial/summary?year=${year}`)],
      events: [api.get('/reports/events/summary')],
    };
    const load = calls[activeTab] || [];
    Promise.all(load).then(results => {
      if (activeTab === 'membership') { setByType(results[0].data); setBySalesperson(results[1].data); }
      if (activeTab === 'financial') { setFinancialSummary(results[0].data); }
      if (activeTab === 'events') { setEventsSummary(results[0].data); }
    }).catch(console.error).finally(() => setLoading(false));
  }, [activeTab]);

  const fmt = (n) => `$${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;

  const handleExport = async (kind) => {
    try {
      const res = await api.get(`/reports/export/${kind}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `njjba_${kind}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to export.');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Reports</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => handleExport('members')} className="btn btn-secondary btn-sm">Export Members CSV</button>
          <button type="button" onClick={() => handleExport('contacts')} className="btn btn-secondary btn-sm">Export Contacts CSV</button>
        </div>
      </div>

      <div className="tabs">
        {['membership','financial','events','sponsors','tickets','payments'].map(tab => (
          <button key={tab} className={`tab-btn${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}>
            {{ sponsors: 'Sponsors (by date)', tickets: 'Tickets (by date)', payments: 'Payments (by date)' }[tab] || tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {['sponsors', 'tickets', 'payments'].includes(activeTab) && <ActivityList key={activeTab} kind={activeTab} />}
      {loading && !['sponsors', 'tickets', 'payments'].includes(activeTab) && <div className="loading-page" style={{ minHeight: 200 }}><div className="spinner" /></div>}

      {!loading && activeTab === 'membership' && (
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><span className="card-title">Members by Type</span></div>
            <div style={{ height: 250, padding: '16px 20px' }}>
              <ResponsiveContainer>
                <BarChart data={byType}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="membership_type" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="active_count" name="Active" fill="#1a3a5c" radius={[4,4,0,0]} />
                  <Bar dataKey="grace_count" name="Grace" fill="#d97706" radius={[4,4,0,0]} />
                  <Bar dataKey="lapsed_count" name="Lapsed" fill="#dc2626" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="table-wrap" style={{ border: 'none' }}>
              <table>
                <thead><tr><th>Type</th><th>Annual Dues</th><th>Active</th><th>Grace</th><th>Lapsed</th><th>Potential Revenue</th></tr></thead>
                <tbody>
                  {byType.map((row, i) => (
                    <tr key={i}>
                      <td className="td-primary">{row.membership_type}</td>
                      <td>{fmt(row.annual_dues)}</td>
                      <td style={{ color: 'var(--success)', fontWeight: 600 }}>{row.active_count}</td>
                      <td style={{ color: 'var(--warning)' }}>{row.grace_count}</td>
                      <td style={{ color: 'var(--danger)' }}>{row.lapsed_count}</td>
                      <td style={{ fontWeight: 500 }}>{fmt(row.potential_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">By Salesperson</span></div>
            <div className="table-wrap" style={{ border: 'none' }}>
              <table>
                <thead><tr><th>Salesperson</th><th>Active</th><th>At Risk</th><th>Total</th><th>Active Dues Value</th></tr></thead>
                <tbody>
                  {bySalesperson.map((row, i) => (
                    <tr key={i}>
                      <td className="td-primary">{row.full_name} <span style={{ color: 'var(--gray-400)', fontSize: '0.78rem' }}>({row.initials})</span></td>
                      <td style={{ color: 'var(--success)', fontWeight: 600 }}>{row.active_members}</td>
                      <td style={{ color: 'var(--warning)' }}>{row.at_risk_members}</td>
                      <td>{row.total_members}</td>
                      <td style={{ fontWeight: 500 }}>{fmt(row.active_dues_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!loading && activeTab === 'financial' && financialSummary && (
        <div>
          <div className="stat-grid" style={{ marginBottom: 20 }}>
            <div className="stat-card"><div className="stat-label">YTD Collected</div><div className="stat-value">{fmt(financialSummary.ytd?.ytd_collected)}</div></div>
            <div className="stat-card"><div className="stat-label">Payments</div><div className="stat-value">{financialSummary.ytd?.ytd_payments}</div></div>
          </div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><span className="card-title">Monthly Revenue {year}</span></div>
            <div style={{ height: 260, padding: '16px 20px' }}>
              <ResponsiveContainer>
                <BarChart data={financialSummary.monthlyBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={m => m?.trim().substring(0,3)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => fmt(v)} />
                  <Bar dataKey="total_collected" name="Collected" fill="#1a3a5c" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="card">
            <div className="card-header"><span className="card-title">By Invoice Type</span></div>
            <div className="table-wrap" style={{ border: 'none' }}>
              <table>
                <thead><tr><th>Type</th><th>Invoices</th><th>Invoiced</th><th>Collected</th><th>Outstanding</th></tr></thead>
                <tbody>
                  {financialSummary.byInvoiceType?.map((row, i) => (
                    <tr key={i}>
                      <td className="td-primary" style={{ textTransform: 'capitalize' }}>{row.invoice_type?.replace('_',' ')}</td>
                      <td>{row.invoice_count}</td>
                      <td>{fmt(row.total_invoiced)}</td>
                      <td style={{ color: 'var(--success)' }}>{fmt(row.total_collected)}</td>
                      <td style={{ color: parseFloat(row.total_outstanding) > 0 ? 'var(--danger)' : undefined }}>{fmt(row.total_outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!loading && activeTab === 'events' && (
        <div className="card">
          <div className="card-header"><span className="card-title">Event Summary</span></div>
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Event</th><th>Date</th><th>Tickets</th><th>Attended</th><th>Ticket Rev.</th><th>Sponsor Rev.</th><th>Total</th></tr></thead>
              <tbody>
                {eventsSummary.length === 0 ? <tr><td colSpan={7}><div className="empty-state" style={{ padding: 20 }}><p>No events</p></div></td></tr>
                : eventsSummary.map((ev, i) => (
                  <tr key={i}>
                    <td className="td-primary">{ev.name}</td>
                    <td style={{ fontSize: '0.8rem' }}>{fmtDate(ev.event_date)}</td>
                    <td>{ev.tickets_sold || 0}</td>
                    <td>{ev.attended || 0}</td>
                    <td>{fmt(ev.ticket_revenue)}</td>
                    <td>{fmt(ev.sponsor_revenue)}</td>
                    <td style={{ fontWeight: 600 }}>{fmt(ev.total_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
